import { CalendarEvent, CalendarEventTimesChangedEvent, CalendarView } from 'angular-calendar';
import { filter, Subject, take, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth/services/auth/auth.service';
import { IUserAndOrganisationUser, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { IOrganisation } from '@organisations/models/organisations';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { OrganisationUserService } from '@organisations/services/organisation-user/organisation-user.service';
import { CalendarEventColours } from '@schedule/lib/classes/event-colours';
import { CalendarEmployeeViewComponent } from '@schedule/lib/components/calendar-employee-view/calendar-employee-view.component';
import { CalendarOwnerViewComponent } from '@schedule/lib/components/calendar-owner-view/calendar-owner-view.component';
import { ISearchableEmployee } from '@schedule/lib/models/employee';
import { ScheduledEvent } from '@schedule/models/calendar/calendar.model';
import { ScheduleRoutes } from '@schedule/schedule.routes';
import { CalendarSettings, CalendarSettingsService } from '@schedule/services/calendar/calendar-settings/calendar-settings.service';
import { CalendarService } from '@schedule/services/calendar/calendar.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { PageTitleComponent } from '@shared/components/sections/page-title/page-title.component';
import { ModalService } from '@shared/services/modal/modal.service';

@Component({
	selector: 'app-schedule',
	imports: [
		CommonModule,
		FormsModule,
		PageTitleComponent,
		LoadingStateComponent,
		CalendarOwnerViewComponent,
		CalendarEmployeeViewComponent,
	],
	templateUrl: './schedule.component.html',
	styleUrl: './schedule.component.scss',
})
export class ScheduleComponent implements OnInit, OnDestroy {
	destroyed$ = new EventEmitter<void>();

	isOwner = false;
	isLoading = true;
	eventsLoading = true;

	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private modalService = inject(ModalService);
	private authService = inject(AuthService);

	// Organisation Data
	private organisation: DocumentReference<IOrganisation>;
	private organisationUserService = inject(OrganisationUserService);
	private organisationSelectedService = inject(OrganisationSelectedService);

	activeOrganisationUsers: Array<IUserAndOrganisationUser> = [];
	employees: Array<ISearchableEmployee> = [];

	selectedEmployeeId: string | null = null;

	// Calendar Data
	private calendarService = inject(CalendarService);
	private calendarSettingsService = inject(CalendarSettingsService);
	// Calendar Events
	protected refresh = new Subject<void>();

	view: CalendarView = CalendarView.Week;
	calendarSettings: CalendarSettings;

	// View Date and Date Ranges
	viewDate: Date = new Date();
	private eventsStartDate: Date = new Date();
	private eventsEndDate: Date = new Date();

	// Scheduled Events
	events: CalendarEvent<ScheduledEvent>[] = [];

	constructor() {
		const activeRoute = this.route.snapshot.url.pop()?.path;
		switch (activeRoute) {
			case ScheduleRoutes.MONTH:
				this.view = CalendarView.Month;
				break;
			default:
			case ScheduleRoutes.WEEK:
				this.view = CalendarView.Week;
				break;
			case ScheduleRoutes.DAY:
				this.view = CalendarView.Day;
				break;
		}
	}

	ngOnInit(): void {
		this.calendarSettings = this.calendarSettingsService.getDefaultSettings();
		this.subscribeToSelectedOrganisation();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	subscribeToSelectedOrganisation(): void {
		this.organisationSelectedService.selectedOrganisation
			.pipe(
				takeUntil(this.destroyed$),
				filter((o) => o !== null),
			)
			.subscribe((selectedOrg) => {
				const accessLevel = selectedOrg?.organisationUser.accessLevel;
				this.isOwner = [OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(accessLevel);
				if (!this.isOwner) {
					this.selectedEmployeeId = this.authService.currentUser ? this.authService.currentUser.uid : null;
				}
				this.organisation = selectedOrg?.organisationUser.organisation as DocumentReference<IOrganisation>;
				this.fetchOrganisationUsers(this.organisation);
				this.calculateQueryDateRanges();
			});
	}

	private fetchOrganisationUsers(organisationRef: DocumentReference<IOrganisation>): void {
		this.organisationUserService
			.activeUsersByOrganisationRef(organisationRef)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((organisationUsers) => {
				this.activeOrganisationUsers = organisationUsers;
				this.employees = organisationUsers.map((u) => ({
					userId: u.user.id as string,
					email: u.user.email as string,
					displayName: u.user.displayName as string,
				}));
			});
	}

	private getCalendarEvents(organisationRef: DocumentReference<IOrganisation>): void {
		this.eventsLoading = true;
		this.calendarService
			.getCalendarEvents(organisationRef, this.eventsStartDate, this.eventsEndDate, this.selectedEmployeeId)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((events) => {
				this.events = events.map((event) => {
					const meta = event!.meta;
					return {
						...event,
						resizable: {
							beforeStart: true,
							afterEnd: true,
						},
						draggable: true,
						meta: meta,
						color: CalendarEventColours.scheduledAssessment(meta!.scheduledAssessmentStatus),
					};
				});
				this.refresh.next();
				this.isLoading = false;
				this.eventsLoading = false;
			});
	}

	/**
	 * Calculates the start and end dates for the current view based on the `viewDate`.
	 * If the calculated start and end dates differ from the existing `eventsStartDate` and `eventsEndDate`,
	 * it updates these dates and fetches the calendar events for the new date range.
	 *
	 * This allows us to use pagination to query events for the current month/week/day
	 */
	private calculateQueryDateRanges(): void {
		let newStartDate: Date;
		let newEndDate: Date;
		let dayOffset: number;
		switch (this.view) {
			case CalendarView.Day:
				newStartDate = new Date(this.viewDate);
				newEndDate = new Date(this.viewDate);
				newStartDate.setHours(0, 0, 0, 0);
				newEndDate.setHours(23, 59, 59, 999);
				break;
			case CalendarView.Week:
			default:
				dayOffset = this.viewDate.getDay() + ((7 - this.calendarSettings.weekStartsOn) % 7);
				newStartDate = new Date(this.viewDate);
				newStartDate.setDate(this.viewDate.getDate() - dayOffset);
				newStartDate.setHours(0, 0, 0, 0);

				newEndDate = new Date(this.viewDate);
				newEndDate.setDate(this.viewDate.getDate() + (6 - dayOffset));
				newEndDate.setHours(23, 59, 59, 999);
				break;
			case CalendarView.Month:
				newStartDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);
				newEndDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 0);

				// Ensure Month View also fetches events from the previous month
				// and the next month to ensure we have a full month of events
				newStartDate.setDate(newStartDate.getDate() - 6);
				newEndDate.setDate(newEndDate.getDate() + 6);
				newEndDate.setHours(23, 59, 59, 999);
				break;
		}

		this.eventsStartDate = newStartDate;
		this.eventsEndDate = newEndDate;
		this.getCalendarEvents(this.organisation);
	}

	viewChanged(view: CalendarView) {
		this.view = view;
		switch (view) {
			case CalendarView.Month:
				this.router.navigate([ScheduleRoutes.MONTH], { relativeTo: this.route });
				break;
			case CalendarView.Week:
				this.router.navigate([ScheduleRoutes.WEEK], { relativeTo: this.route });
				break;
			case CalendarView.Day:
				this.router.navigate([ScheduleRoutes.DAY], { relativeTo: this.route });
				break;
			default:
				this.viewChanged(CalendarView.Week);
				break;
		}
	}

	viewDateChange(viewDate: Date) {
		this.viewDate = viewDate;
		this.calculateQueryDateRanges();
	}

	selectedEmployeeChange(): void {
		this.getCalendarEvents(this.organisation);
	}
	/**
	 * Allow events to be dragged and dropped freely without RULES
	 */
	eventTimesChanged(changedEvent: CalendarEventTimesChangedEvent): void {
		const eventFind = this.events.find((event) => event === changedEvent.event);

		const startTimeChanged = eventFind!.start.getTime() === changedEvent.newStart.getTime();
		const endTimeChanged = eventFind!.end!.getTime() === changedEvent.newEnd!.getTime();

		// Ensure the time has actually changed to avoid misclicks
		if (!eventFind || (startTimeChanged && endTimeChanged)) {
			return;
		}

		const modalRef = this.modalService.open(ConfirmationModalComponent, false, 'md');
		const componentInstance = modalRef.componentInstance as ConfirmationModalComponent;
		componentInstance.title = 'Are you sure you want to change the time of this event?';
		componentInstance.contentText = `Proposed time: ${Intl.DateTimeFormat('en-ZA', {
			weekday: 'short',
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		}).formatRange(changedEvent.newStart, changedEvent.newEnd!)}`;
		componentInstance.confirmAction.pipe(take(1)).subscribe(() => {
			this.events = this.events.map((iEvent) => {
				if (iEvent === changedEvent.event) {
					return {
						...changedEvent.event,
						start: changedEvent.newStart,
						end: changedEvent.newEnd,
					};
				}
				return iEvent;
			});
			this.calendarService.updateScheduledAssessmentTimes(changedEvent.event.meta!, changedEvent.newStart, changedEvent.newEnd!);
		});
	}
}
